import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Downline tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function DownlineTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  return (
    <div className="space-y-4" data-testid="downline-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-4">Downline Management</h2>
            
                  <div className="wm-card p-6 rounded-lg space-y-4">
                    <h3 className="font-semibold text-foreground">Move Member Between Teams</h3>
                    <p className="text-sm text-muted-foreground">Transfer a member from one recruiter's downline to another.</p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-foreground">Member (select from users)</Label>
                        <select
                          value={moveDownlineData.member_id}
                          onChange={(e) => setMoveDownlineData({ ...moveDownlineData, member_id: e.target.value })}
                          className="w-full bg-input border border-border rounded-md p-2 text-foreground mt-1"
                          data-testid="move-member-select"
                        >
                          <option value="">Select member...</option>
                          {users.filter(u => !u.is_admin).map(u => (
                            <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-foreground">New Recruiter Referral Code</Label>
                        <Input
                          value={moveDownlineData.new_referral_code}
                          onChange={(e) => setMoveDownlineData({ ...moveDownlineData, new_referral_code: e.target.value })}
                          placeholder="e.g., ABC12345"
                          className="bg-input text-foreground mt-1"
                          data-testid="new-referral-code-input"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          className="bg-cyan-600 text-white hover:bg-cyan-700"
                          onClick={async () => {
                            if (!moveDownlineData.member_id || !moveDownlineData.new_referral_code) {
                              toast.error('Select a member and enter a referral code');
                              return;
                            }
                            try {
                              await axios.post(`${API_URL}/api/admin/move-downline`, moveDownlineData, { headers: getAuthHeader() });
                              toast.success('Member moved successfully');
                              setMoveDownlineData({ member_id: '', new_referral_code: '' });
                              fetchAllData();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to move member');
                            }
                          }}
                          data-testid="move-downline-btn"
                        >
                          Move Member
                        </Button>
                      </div>
                    </div>
                  </div>
            
                  <div className="wm-card p-6 rounded-lg">
                    <h3 className="font-semibold text-foreground mb-4">All Members & Their Referral Codes</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 text-muted-foreground">Name</th>
                            <th className="text-left py-2 text-muted-foreground">Tier</th>
                            <th className="text-left py-2 text-muted-foreground">Referral Code</th>
                            <th className="text-left py-2 text-muted-foreground">Referred By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.filter(u => !u.is_admin).map(u => (
                            <tr key={u.id} className="border-b border-border/30">
                              <td className="py-2 text-foreground">{u.name}</td>
                              <td className="py-2"><span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary capitalize">{u.membership_tier}</span></td>
                              <td className="py-2 text-muted-foreground font-mono text-xs">{u.referral_code || '—'}</td>
                              <td className="py-2 text-muted-foreground font-mono text-xs">{u.referred_by || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
    </div>
  );
}
